import type { Team } from '@/types/team.types';

export const UserRoleId = {
  ADMIN: 'ADMIN',
  ACCOUNTANT: 'ACCOUNTANT',
  OPERATOR: 'OPERATOR',
  SERVER: 'SERVER',
} as const;
export type UserRoleId = (typeof UserRoleId)[keyof typeof UserRoleId];

// Aliases keep the names call sites used with the deprecated SDK.
export const UserResponseSchemaRoleId = UserRoleId;
export type UserResponseSchemaRoleId = UserRoleId;
export const UserInviteRequestRoleId = UserRoleId;
export type UserInviteRequestRoleId = UserRoleId;

export type UserResponseSchemaStatus = 'ACTIVE' | 'PENDING' | 'INACTIVE' | 'EXPIRED';
export const UserUpdateByIdRequestStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;
export type UserUpdateByIdRequestStatus = (typeof UserUpdateByIdRequestStatus)[keyof typeof UserUpdateByIdRequestStatus];

export interface RoleResponseSchema {
  createdAt: string | null;
  createdById?: string | null;
  deletedAt: string | null;
  name?: string;
  roleId: string;
  updatedAt: string | null;
}

export interface UserResponseSchema {
  activatedAt: string | null;
  cancelledAt: string | null;
  city?: string | null;
  code: string;
  createdAt: string | null;
  deactivatedAt: string | null;
  deletedAt: string | null;
  email: string;
  firstName: string;
  invitedAt: string | null;
  invitedById: string | null;
  isOnboarded?: boolean;
  lastName: string;
  phone?: string | null;
  primaryAddress?: string | null;
  roleId: UserResponseSchemaRoleId;
  roles: RoleResponseSchema;
  secondaryAddress?: string | null;
  secondaryEmail?: string | null;
  state?: string | null;
  status: UserResponseSchemaStatus;
  updatedAt: string | null;
  userId: string;
  zipCode?: string | null;
}

export type UserGetAllParams = {
  pageNo?: number;
  pageSize?: number;
  sortField?: 'userName' | 'email' | 'roleId' | 'status' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  search?: string;
  statusFilter?: 'INVITED' | 'ACTIVATED';
  roleId?: string;
  code?: string;
  email?: string;
  userName?: string;
};

export interface UserGetAllResponse {
  users: UserResponseSchema[];
  totalRows: number;
}

interface UserProfileFields {
  city?: string | null;
  firstName: string;
  lastName: string;
  phone?: string | null;
  primaryAddress?: string | null;
  secondaryAddress?: string | null;
  secondaryEmail?: string | null;
  state?: string | null;
  zipCode?: string | null;
}

export interface UserInviteRequest extends UserProfileFields {
  email: string;
  roleId: UserInviteRequestRoleId;
}

export interface UserInviteResponse {
  message?: string;
}

export interface UserUpdateByIdRequest extends UserProfileFields {
  roleId: UserRoleId;
  status: UserUpdateByIdRequestStatus;
}

export type UserToggleActivationRequestAction = 'ACTIVATE' | 'DEACTIVATE' | 'REINVITE' | 'CANCEL';

export interface UserToggleActivationRequest {
  action: UserToggleActivationRequestAction;
}

export interface UserToggleResponse {
  success: true;
  message: string;
  status: UserResponseSchemaStatus | null;
  userId?: string | null;
}

export interface UserInviteRequestSchema extends UserInviteRequest {
  teams?:
    | {
        teamId: string;
        isSupervisor: boolean;
      }[]
    | null;
}

export interface ExtendedUserResponseSchema extends UserResponseSchema {
  status: UserUpdateByIdRequestStatus;
  teams: Team[];
}

export interface UserUpdateRequestSchema extends UserUpdateByIdRequest {
  teams?: {
    create?:
      | {
          teamId: string;
          isSupervisor: boolean;
        }[]
      | null;

    update?:
      | {
          teamId: string;
          isSupervisor: boolean;
        }[]
      | null;

    delete?: string[] | null;
  };
}

// Log-in history (DGR-1835). The API never returns IP or browser/device details.
export type LoginHistoryView = 'ALL' | 'DAILY_SUMMARY';

export type LoginHistorySession = {
  id: string;
  loginAt: string;
  logoutAt: string | null;
  logoutReason: 'MANUAL' | 'INACTIVITY' | 'BROWSER_CLOSED' | 'SESSION_EXPIRED' | null;
};

export type LoginHistoryDay = {
  date: string;
  firstLoginAt: string | null;
  lastLogoutAt: string | null;
};

export type UserLoginHistoryResponse =
  | { view: 'ALL'; sessions: LoginHistorySession[]; totalRows: number }
  | { view: 'DAILY_SUMMARY'; days: LoginHistoryDay[]; totalRows: number };
